import { FC, ReactNode, useMemo } from "react";
import { AuthTokenContext, AuthTokenContextValue } from "./AuthTokenContext";

/**
 * Props for the AuthTokenProvider component.
 */
export interface AuthTokenProviderProps {
  /**
   * The authentication token to provide to child components.
   */
  readonly authToken: string | null;

  /**
   * The child components to render.
   */
  readonly children: ReactNode;
}

/**
 * Provides an authentication token to child components via context.
 * This allows useRemoteTableDataSource and similar hooks to automatically
 * include Authorization headers without requiring explicit authToken props.
 *
 * @example
 * ```tsx
 * // In your app root or shell:
 * const [authToken] = useAuthToken();
 *
 * return (
 *   <AuthTokenProvider authToken={authToken}>
 *     <App />
 *   </AuthTokenProvider>
 * );
 * ```
 */
export const AuthTokenProvider: FC<AuthTokenProviderProps> = ({ authToken, children }) => {
  const value = useMemo<AuthTokenContextValue>(() => ({ authToken }), [authToken]);

  return <AuthTokenContext.Provider value={value}>{children}</AuthTokenContext.Provider>;
};
